import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sampleReceiptsApi } from '../../api/endpoints.js';
import { ActionButton, ErrorState } from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { openPdf, printPdf, requirePdf, saveBlob } from '../reports/report-parts.js';

type DocumentLanguage = 'ar' | 'en';

/** The query key of one receipt's document in one language. */
export const receiptDocumentKey = (id: string, language: DocumentLanguage) =>
  ['sample-receipt-document', id, language] as const;

/**
 * The language of the receipt document: the interface's, read at the moment
 * of use (D221). The receipt document exists in Arabic and English; any other
 * interface language gets the English one.
 */
export function receiptDocumentLanguage(): DocumentLanguage {
  return currentLocale() === 'ar' ? 'ar' : 'en';
}

/**
 * View PDF, Print Receipt and Download PDF (D213, D221).
 *
 * All three are the SAME PDF, rendered by the server from the receipt
 * document's own template on each request — never this web page, and never
 * stored. Printing a receipt records nothing and changes nothing.
 *
 * ONE LANGUAGE SOURCE: each action reads the interface's CURRENT language when
 * it is pressed, so the three can never disagree and a language switch is
 * followed without a reload. There is deliberately no separate selector.
 */
export function ReceiptDocumentActions({
  receiptId,
  receiptNumber,
  size,
}: {
  readonly receiptId: string;
  readonly receiptNumber: string;
  readonly size?: 'sm' | 'md';
}) {
  // Subscribes to language changes, so the labels follow a switch too.
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /** The PDF just fetched in this language, or a fresh one — the same bytes. */
  const fetchDocument = async (language: DocumentLanguage) => {
    const blob = await queryClient.fetchQuery({
      queryKey: receiptDocumentKey(receiptId, language),
      queryFn: () => sampleReceiptsApi.document(receiptId, language).then(requirePdf),
      staleTime: 30_000,
    });
    return { blob, language };
  };

  const view = useMutation({
    mutationFn: fetchDocument,
    onSuccess: ({ blob }) => openPdf(blob),
  });

  const print = useMutation({
    mutationFn: fetchDocument,
    onSuccess: ({ blob }) => printPdf(blob),
  });

  const download = useMutation({
    mutationFn: fetchDocument,
    onSuccess: ({ blob, language }) => saveBlob(blob, `${receiptNumber}-${language}.pdf`),
  });

  const error = view.error ?? print.error ?? download.error;
  const sized = size ? { size } : {};

  return (
    <div className="flex flex-col gap-stack">
      <div className="flex flex-wrap items-center gap-inline">
        <ActionButton
          priority="secondary"
          {...sized}
          onPress={() => view.mutate(receiptDocumentLanguage())}
          isLoading={view.isPending}
        >
          {t('receipts.viewPdf')}
        </ActionButton>
        <ActionButton
          priority="secondary"
          {...sized}
          onPress={() => print.mutate(receiptDocumentLanguage())}
          isLoading={print.isPending}
        >
          {t('receipts.print')}
        </ActionButton>
        <ActionButton
          priority="secondary"
          {...sized}
          onPress={() => download.mutate(receiptDocumentLanguage())}
          isLoading={download.isPending}
        >
          {t('receipts.downloadPdf')}
        </ActionButton>
      </div>
      {error ? <ErrorState error={error} /> : null}
    </div>
  );
}
